import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

import ConfigurableDataTable from 'app/admin/common/component/ConfigurableDataTable';
import { Button } from './Pipeline';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function DataValidationDetailsPopup() {
    const [rows, setRows] = useState([]);

    const { popup, setPopup, cleanMeasurementDataList, mergedMeasurementDataList } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    function collectReaderSynthRows(list) {
        const out = [];
        for (const entry of (Array.isArray(list) ? list : [])) {
            const ts = entry?.ts ?? null;
            const meta = entry?.meta ?? null;

            const synth = (typeof meta?.synthetic_data === 'boolean') ? meta.synthetic_data : undefined;
            const mergerKey = meta ? Object.keys(meta).find(k => k.startsWith('merger:')) : null;
            const inputs = mergerKey ? meta?.[mergerKey]?.inputs : null;

            const readerSet = new Set();
            if (inputs && typeof inputs === 'object') {
                for (const [, v] of Object.entries(inputs)) {
                    const name = v?.data_origin?.data_processor_name || v?.data_origin?.data_processor || 'unknown';
                    readerSet.add(String(name));
                }
            }

            out.push({ ts, readers: Array.from(readerSet), synth });
        }
        return out;
    }

    function syntheticCell(row) {
        const v = row?.synth;
        const bg = (v === true) ? 'bg-red-100' : (v === false ? 'bg-green-100' : 'bg-gray-200');
        const dim = (v !== true && v !== false) ? 'opacity-25' : '';
        return (
            <div className={'flex items-center text-12 px-5 py-3 rounded ' + bg + ' ' + dim}>
                {v === true ? 'true' : v === false ? 'false' : ' -'}
            </div>
        );
    }

    function readersCell(row) {
        const list = Array.isArray(row?.readers) ? row.readers : [];
        if (!list.length) return <div className='text-12 px-5 py-3 rounded opacity-25'>-</div>;
        return (
            <div className='flex flex-col text-12 px-5 py-3 rounded'>
                {list.map((name, i) => (<div key={i} className='leading-4'>{name}</div>))}
            </div>
        );
    }

    useEffect(() => {
        if (mergedMeasurementDataList)
            setRows(collectReaderSynthRows(mergedMeasurementDataList));
    }, [mergedMeasurementDataList]);

    const columns = useMemo(() => {
        return [
            { key: 'ts', header: 'Timestamp', sortable: true, format: (v) => v ? format(new Date(v), 'yyyy.MM.dd', { locale: hu }) : '-' },
            { key: 'readers', header: 'Reader', accessor: (row) => readersCell(row) },
            { key: 'synth', header: 'Synthetic', accessor: (row) => syntheticCell(row) }
        ];
    }, []);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open={popup === 'DATA_VALIDATION_DETAILS'} fullWidth maxWidth={rows.length ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className='flex items-center justify-between gap-32 px-32'>
                        <div className='whitespace-nowrap'>Data Validation – Readers & Synthetic</div>
                        <Button label='Close' onClick={() => setPopup(null)} />
                    </div>
                </DialogTitle>
                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: rows.length ? '520px' : 'auto' }}>
                    <ConfigurableDataTable rows={rows} columns={columns} searchable={false} />
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}